import {
    Box,
    Breadcrumbs,
    Card,
    CardContent,
    Chip,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants/listPagination';
import { isMasterUser } from '../../helpers/authSession';
import { formatCurrencyInCents } from '../../helpers/formatters';
import {
    formatProductionDurationMinutes,
    getJobStatusBadgeProps,
    statusLabel,
} from '../../helpers/jobs';
import { productKindLabel } from '../../helpers/orderStages';

const TABS = [
    { id: 'printers', label: 'Impressoras' },
    { id: 'jobs', label: 'Impressões' },
    { id: 'products', label: 'Produtos' },
];

function formatSuccessRate(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return '—';
    }
    return `${n.toFixed(1).replace('.', ',')}%`;
}

export default function UserAssetsPage() {
    const { uuid } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const activeTab = TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'printers';

    const [user, setUser] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [userError, setUserError] = useState(false);

    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const loadUser = useCallback(async () => {
        setUserLoading(true);
        setUserError(false);
        try {
            const { data } = await api.get(`/users/${uuid}`);
            setUser(data?.data || data || null);
        } catch {
            setUserError(true);
            setUser(null);
        } finally {
            setUserLoading(false);
        }
    }, [uuid]);

    const loadList = useCallback(async () => {
        setListLoading(true);
        setListError(false);
        try {
            const { data } = await api.get(`/users/${uuid}/${activeTab}`, {
                params: { page, limit: DEFAULT_LIST_PAGE_SIZE },
            });
            const payload = data?.data || {};
            if (activeTab === 'printers') {
                setRows(payload.printers || []);
            } else if (activeTab === 'jobs') {
                setRows(payload.jobs || []);
            } else {
                setRows(payload.products || []);
            }
            setTotalPages(Math.max(1, Number(payload.total_pages) || 1));
            setTotal(Number(payload.total) || 0);
        } catch {
            setListError(true);
            setRows([]);
            setTotalPages(1);
            setTotal(0);
        } finally {
            setListLoading(false);
        }
    }, [uuid, activeTab, page]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    if (!isMasterUser()) {
        return <Navigate to="/admin" replace />;
    }

    const handleTabChange = (_event, nextTab) => {
        setPage(1);
        setSearchParams(nextTab === 'printers' ? {} : { tab: nextTab });
    };

    const userName = user?.name || user?.email || 'Usuário';

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link to="/admin/users" style={{ color: 'inherit' }}>
                    Usuários
                </Link>
                <Typography color="text.primary">Dados de {userName}</Typography>
            </Breadcrumbs>

            <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                    Dados do usuário
                </Typography>
                {userLoading ? (
                    <Typography variant="body2" color="text.secondary">
                        Carregando...
                    </Typography>
                ) : userError ? (
                    <Typography variant="body2" color="error">
                        Não foi possível carregar o usuário.
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {user?.name || '—'} · {user?.email || '—'}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (somente leitura)
                        </Typography>
                    </Typography>
                )}
            </Stack>

            <Card variant="outlined" sx={{ mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {TABS.map((tab) => (
                        <Tab key={tab.id} value={tab.id} label={tab.label} />
                    ))}
                </Tabs>
            </Card>

            <Card>
                <CardContent>
                    {listLoading ? (
                        <Loading />
                    ) : listError ? (
                        <DidNotLoad onRetry={loadList} />
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {total} {total === 1 ? 'registro' : 'registros'}
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    {activeTab === 'printers' && (
                                        <>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Nome</TableCell>
                                                    <TableCell>Marca</TableCell>
                                                    <TableCell>Modelo</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell align="right">Valor pago</TableCell>
                                                    <TableCell align="right">Taxa de sucesso</TableCell>
                                                    <TableCell align="right">Impressões</TableCell>
                                                    <TableCell align="right">Objetos</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={8}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Nenhuma impressora cadastrada.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    rows.map((printer) => (
                                                        <TableRow key={printer.uuid || printer.id} hover>
                                                            <TableCell>{printer.name || '—'}</TableCell>
                                                            <TableCell>{printer.brand || '—'}</TableCell>
                                                            <TableCell>{printer.model || '—'}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={printer.active === false ? 'Inativa' : 'Ativa'}
                                                                    color={printer.active === false ? 'default' : 'success'}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {formatCurrencyInCents(printer.price)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {formatSuccessRate(printer.success_rate)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {printer.total_jobs ?? 0}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {printer.total_objects ?? 0}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </>
                                    )}

                                    {activeTab === 'jobs' && (
                                        <>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Arquivo</TableCell>
                                                    <TableCell>Impressora</TableCell>
                                                    <TableCell>Duração estimada</TableCell>
                                                    <TableCell align="right">Filamento estimado</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Nenhuma impressão cadastrada.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    rows.map((job) => {
                                                        const chip = getJobStatusBadgeProps(job.status);
                                                        return (
                                                            <TableRow key={job.uuid || job.id} hover>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        noWrap
                                                                        title={job.file_name}
                                                                        sx={{ maxWidth: 280 }}
                                                                    >
                                                                        {job.file_name || '—'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>{job.printer_name || '—'}</TableCell>
                                                                <TableCell>
                                                                    {formatProductionDurationMinutes(
                                                                        job.estimated_duration_minutes,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {`${job.estimated_filament_consumption_grams ?? 0} g`}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        size="small"
                                                                        label={chip?.label || statusLabel(job.status)}
                                                                        color={chip?.color || 'default'}
                                                                        sx={chip?.sx}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                        </>
                                    )}

                                    {activeTab === 'products' && (
                                        <>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Nome</TableCell>
                                                    <TableCell align="right">Custo de produção</TableCell>
                                                    <TableCell>Tipo</TableCell>
                                                    <TableCell align="right">Preço de venda</TableCell>
                                                    <TableCell align="right">Estoque</TableCell>
                                                    <TableCell align="right">Total vendido</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Nenhum produto cadastrado.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    rows.map((product) => (
                                                        <TableRow key={product.uuid || product.id} hover>
                                                            <TableCell>{product.name || '—'}</TableCell>
                                                            <TableCell align="right">
                                                                {formatCurrencyInCents(product.cost_price)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {productKindLabel(product.kind)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {formatCurrencyInCents(product.base_price)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {product.stock_quantity ?? 0}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {product.total_sales_quantity ?? 0}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </>
                                    )}
                                </Table>
                            </TableContainer>

                            {totalPages > 1 && (
                                <CustomPagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    onPageChange={(next) => setPage(next)}
                                />
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
